import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { titleCase } from "../lib/format";

/** The navigation from build guide / 03, filtered by what the role may see. */
const NAV = [
  { to: "/", label: "Dashboard", permission: "jobs.read", end: true },
  { to: "/appointments", label: "Appointments", permission: "appointments.read" },
  { to: "/customers", label: "Customers & vehicles", permission: "customers.read" },
  { to: "/board", label: "Work board", permission: "jobs.read" },
  { to: "/pos", label: "POS", permission: "payments.collect" },
  { to: "/timeclock", label: "Time clock", permission: "timeclock.use" },
  { to: "/parts-catalog", label: "Parts & vehicles", permission: "jobs.read" },
  { to: "/finance", label: "Finance", permission: "finance.read" },
  { to: "/reports", label: "Reports", permission: "reports.sales" },
  { to: "/settings", label: "Settings", permission: "settings.read" },
];

export function Layout() {
  const { user, signOut, can } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const visible = NAV.filter((item) => can(item.permission));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
            >
              ☰
            </button>
            <div>
              <p className="text-sm font-semibold leading-tight text-ink">
                {user?.organization.name}
              </p>
              <p className="text-xs text-slate-500">
                {user?.location?.name ?? "All locations"}
              </p>
            </div>
          </div>

          <nav className="hidden items-center gap-1 lg:flex">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition ${
                    isActive ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight text-ink">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-slate-500">{titleCase(user?.role ?? "")}</p>
            </div>
            <button
              type="button"
              onClick={signOut}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="border-t border-slate-200 bg-white px-4 py-2 lg:hidden">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm font-medium ${
                    isActive ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-ink">{title}</h1>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {action}
    </div>
  );
}
